if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.limit = Number(
        this.dataset.recommendationLimit ||
        (this.dataset.recommendationArea === 'cart-drawer' ? 3 : 8)
      );

      this.sourceFamily =
        this.dataset.sourceFamily || 'unknown';

      this.sourceSize =
        this.dataset.sourceSize || '';

      this.sourceCollections =
        this.pipeSet(this.dataset.sourceCollections);

      this.cartProductIds =
        this.pipeSet(this.dataset.cartProductIds);

      this.isCartDrawer =
        this.dataset.recommendationArea === 'cart-drawer';

      this.preferredRange =
        this.dataset.preferredRange || 'aw26';

      this.shirtOfferActive =
        this.isCartDrawer && this.dataset.shirtOffer === 'true';

      try {
        this.cartSizes = JSON.parse(this.dataset.cartSizes || '[]');
      } catch (error) {
        this.cartSizes = [];
      }

      // Capturing the click prevents the theme's existing quick-cart
      // trigger from opening a second drawer during a direct add.
      if (this.isCartDrawer) {
        this.addEventListener('click', event => this.handleQuickAddClick(event), true);
        this.addEventListener('change', event => this.handleSuggestedSizeChange(event));
      }
    }

    connectedCallback() {
      if (this.dataset.recommendationsLoaded === 'true') return;

      this.dataset.recommendationsLoaded = 'true';
      this.performRecommendations();
    }

    pipeSet(value = '') {
      return new Set(
        String(value)
          .split('|')
          .map(item => item.trim())
          .filter(Boolean)
      );
    }

    normalizeSize(value = '') {
      const raw = String(value)
        .trim()
        .toLowerCase();

      if (!raw) return '';

      const alphaPattern =
        /(xxxs|xxs|xs|xxxl|xxl|xl|s|m|l)/i;

      const parenthetical =
        raw.match(
          new RegExp(`\\(\\s*${alphaPattern.source}\\s*\\)`, 'i')
        );

      if (parenthetical) {
        return parenthetical[1].toLowerCase();
      }

      const standalone =
        raw.match(
          new RegExp(
            `(?:^|[^a-z])${alphaPattern.source}(?:$|[^a-z])`,
            'i'
          )
        );

      if (standalone) {
        return standalone[1].toLowerCase();
      }

      const numeric =
        raw.match(/^\s*(\d{1,3})\s*$/);

      if (numeric) {
        return numeric[1];
      }

      return raw.replace(/[^a-z0-9]+/g, '');
    }

    sizeSystem(value = '') {
      const normalized = this.normalizeSize(value);

      if (!normalized) return 'none';

      if (
        /^(xxxs|xxs|xs|s|m|l|xl|xxl|xxxl)$/.test(
          normalized
        )
      ) {
        return 'alpha';
      }

      if (/^\d{1,3}$/.test(normalized)) {
        return 'numeric';
      }

      return 'other';
    }

    normalizedSizeSet(value = '') {
      return new Set(
        Array.from(this.pipeSet(value))
          .map(size => this.normalizeSize(size))
          .filter(Boolean)
      );
    }

    availableVariants(item) {
      try {
        const parsed = JSON.parse(item.dataset.quickAddVariants || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(variant =>
          /^\d+$/.test(String(variant.id)) &&
          typeof variant.size === 'string'
        );
      } catch (error) {
        return [];
      }
    }

    // Pick a useful starting size but never silently infer that a shirt's
    // size 2 is a trouser size 2 (or map a shirt to a waist measurement).
    // Every assumed size is shown with an inline Change control.
    directAddVariant(item) {
      const variants = this.availableVariants(item);
      if (!variants.length) return null;
      if (item.dataset.hasSizeOption !== 'true') {
        return variants.length === 1 ? variants[0] : null;
      }

      // Products with colour/fit options need the full quick-cart selector.

      // Direct-add is safe when each available size has
      // exactly one variant, even if the product has other options.
      const sizeKeys = variants.map(variant =>
        this.normalizeSize(variant.size)
      );

      if (
        sizeKeys.some(size => !size) ||
        new Set(sizeKeys).size !== variants.length
      ) {
        return null;
      }

      const candidateFamily = this.getFamily(item);
      const candidateSystems = new Set(variants.map(variant =>
        this.sizeSystem(variant.size)
      ));
      const entries = (this.cartSizes || [])
        .filter(entry => entry && typeof entry.size === 'string')
        .map(entry => ({
          family: entry.family || 'unknown',
          size: this.normalizeSize(entry.size)
        }))
        .filter(entry =>
          entry.size && candidateSystems.has(this.sizeSystem(entry.size))
        );

      const sameFamily = entries.filter(entry => entry.family === candidateFamily);
      // Prefer exact same-family size, otherwise a comparable top-size
      // (M -> M). For different garment families, don't map numbers.
      const comparable = sameFamily.length ? sameFamily : entries.filter(entry =>
        this.sizeSystem(entry.size) === 'alpha'
      );
      const sizes = [...new Set(comparable.map(entry => entry.size))];
      if (sizes.length === 1) {
        const matched = variants.find(variant =>
          this.normalizeSize(variant.size) === sizes[0]
        );
        if (matched) return matched;
      }

      // If matching isn't possible, suggest the middle AVAILABLE size.
      // It's explicitly labelled as suggested and always changeable.
      return variants[Math.floor((variants.length - 1) / 2)];
    }

    prepareDirectAddButtons(selected) {
      if (!this.isCartDrawer) return;

      selected.forEach(item => {
        const button = item.querySelector('.fa-cart-recommendation-card__quick-add');
        const sizeRow = item.querySelector('[data-recommendation-size-row]');
        const sizeText = item.querySelector('[data-recommendation-size-text]');
        const select = item.querySelector('[data-recommendation-size-select]');
        const change = item.querySelector('[data-recommendation-change]');
        if (!button) return;

        const variants = this.availableVariants(item);
        const recommended = this.directAddVariant(item);
        const name = item.querySelector('.product-recommendations__item-title')?.textContent.trim() || 'item';
        if (!recommended) {
          delete button.dataset.directAddVariantId;
          button.setAttribute('aria-label', `Choose options for ${name}`);
          button.setAttribute('title', 'Choose options');
          if (sizeRow) sizeRow.hidden = true;
          return;
        }

        button.dataset.directAddVariantId = String(recommended.id);
        const hasSize = item.dataset.hasSizeOption === 'true';
        const sizeLabel = hasSize ? `Size ${recommended.size}` : 'One size';
        button.setAttribute('aria-label', `Add ${name}, ${sizeLabel}, to bag`);
        button.setAttribute('title', `Add ${sizeLabel} to bag`);

        if (!sizeRow || !select || !sizeText || !change) return;
        sizeRow.hidden = !hasSize;
        if (!hasSize) return;

        sizeText.textContent = sizeLabel;
        select.replaceChildren();
        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.size;
          option.selected = String(variant.id) === String(recommended.id);
          select.appendChild(option);
        });
        change.hidden = variants.length < 2;
        select.hidden = true;
      });
    }

    handleQuickAddClick(event) {
      const change = event.target.closest('[data-recommendation-change]');
      if (change && this.contains(change)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const select = change.parentElement.querySelector('[data-recommendation-size-select]');
        if (select) {
          select.hidden = !select.hidden;
          change.setAttribute('aria-expanded', String(!select.hidden));
          if (!select.hidden) select.focus();
        }
        return;
      }

      const button = event.target.closest('.fa-cart-recommendation-card__quick-add');
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.disabled) return;

      const variantId = button.dataset.directAddVariantId;
      if (variantId) {
        this.addVariantToCart(button, variantId);
        return;
      }

      // Preserve the existing quick-cart selector for missing/ambiguous
      // sizes. Pass the button itself as target so SVG clicks work too.
      const quickCart = document.querySelector('quick-cart-drawer');
      if (quickCart && typeof quickCart.fetchProductForQuickCartDrawer === 'function') {
        quickCart.fetchProductForQuickCartDrawer({ target: button });
      }
    }

    handleSuggestedSizeChange(event) {
      const select = event.target.closest('[data-recommendation-size-select]');
      if (!select || !this.contains(select)) return;
      const item = select.closest('.fa-cart-recommendation-card');
      const variant = this.availableVariants(item).find(candidate =>
        String(candidate.id) === select.value
      );
      const button = item?.querySelector('.fa-cart-recommendation-card__quick-add');
      const label = item?.querySelector('[data-recommendation-size-text]');
      if (!variant || !button || !label) return;
      button.dataset.directAddVariantId = String(variant.id);
      label.textContent = `Size ${variant.size}`;
      const name = item.querySelector('.product-recommendations__item-title')?.textContent.trim() || 'item';
      button.setAttribute('aria-label', `Add ${name}, Size ${variant.size}, to bag`);
      button.setAttribute('title', `Add Size ${variant.size} to bag`);
      select.hidden = true;
      const change = item.querySelector('[data-recommendation-change]');
      if (change) change.setAttribute('aria-expanded', 'false');
    }

    setCartMessage(message) {
      const status = this.querySelector('[data-recommendation-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }

    async addVariantToCart(button, variantId) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.getSectionsToRender !== 'function') return;

      button.disabled = true;
      button.classList.add('is--loading');
      button.setAttribute('aria-busy', 'true');
      this.setCartMessage('');

      const sections = drawer.getSectionsToRender().map(section => section.section);
      const endpoint = window.routes?.cart_add_url ||
        `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      let added = false;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname
          })
        });
        const payload = await response.json();
        if (!response.ok || payload.status >= 400 || payload.errors) {
          throw new Error(payload.description || payload.message || 'Could not add this size.');
        }
        added = true;

        // Reuse the exact cart update path used by the theme's own
        // product forms, without reopening the already-visible drawer.
        if (!sections.every(section => typeof payload.sections?.[section] === 'string')) {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', sections.join(','));
          const rendered = await fetch(url.toString(), { headers: { 'Accept': 'application/json' } });
          if (!rendered.ok) throw new Error('Could not refresh the bag.');
          payload.sections = await rendered.json();
        }
        if (!sections.every(section => typeof payload.sections?.[section] === 'string')) {
          throw new Error('Could not refresh the bag.');
        }
        drawer.renderContents(payload, false);
        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
      } catch (error) {
        if (added) {
          // The add succeeded; never retry the POST or risk duplicates.
          window.location.reload();
          return;
        }
        this.setCartMessage(error.message || 'Could not add this item. Please try again.');
      } finally {
        if (button.isConnected) {
          button.disabled = false;
          button.classList.remove('is--loading');
          button.removeAttribute('aria-busy');
        }
      }
    }

    getProductId(item) {
      return String(
        item.dataset.productId ||
        item.getAttribute('href') ||
        ''
      );
    }

    getFamily(item) {
      return item.dataset.family || 'unknown';
    }

    isSizeEligible(item) {
      // Leave PDP/Worn With filtering completely unchanged; those cards
      // intentionally do not carry the extra cart-only variant metadata.
      if (!this.isCartDrawer) return true;
      // The customer can amend an available suggested size in the cart.
      return this.availableVariants(item).length > 0;
    }

    belongsToSelectedPool(item) {
      if (!this.isCartDrawer) return true;

      const collections = this.pipeSet(item.dataset.collections);
      const isCapoPop =
        collections.has('the-sopranos-capo-collection') ||
        collections.has('far-afield-pop');

      // The shirt offer never crosses ranges; the second shirt must
      // qualify for the discount AND be in the same collection family.
      if (this.shirtOfferActive && (
        !collections.has('full-price-shirts') ||
        item.dataset.shirtOfferEligible !== 'true'
      )) return false;

      if (this.preferredRange === 'capo-pop') {
        return isCapoPop;
      }

      return collections.has('aw26') && !isCapoPop;
    }

    isEligible(item) {
      const productId = this.getProductId(item);

      if (!productId || this.cartProductIds.has(productId)) {
        return false;
      }

      return this.belongsToSelectedPool(item) &&
        this.isSizeEligible(item);
    }

    getFamilyPreferences() {
      const preferences = {
        shirts: [
          'trousers',
          'knitwear',
          'outerwear',
          'accessories'
        ],

        trousers: [
          'shirts',
          'knitwear',
          'outerwear',
          'accessories'
        ],

        knitwear: [
          'trousers',
          'shirts',
          'outerwear',
          'accessories'
        ],

        outerwear: [
          'knitwear',
          'trousers',
          'shirts',
          'accessories'
        ],

        jersey: [
          'trousers',
          'outerwear',
          'knitwear',
          'shirts'
        ],

        shorts: [
          'shirts',
          'jersey',
          'knitwear',
          'accessories'
        ],

        accessories: [
          'outerwear',
          'knitwear',
          'shirts',
          'trousers'
        ],

        unknown: [
          'trousers',
          'shirts',
          'knitwear',
          'outerwear',
          'jersey'
        ]
      };

      return (
        preferences[this.sourceFamily] ||
        preferences.unknown
      );
    }

    meaningfulSharedCollections(item) {
      const genericCollections = new Set([
        'all',
        'new-arrivals',
        'shirts',
        'trousers',
        'knitwear',
        'outerwear',
        'overshirts',
        'shorts',
        't-shirts',
        'sweatshirts',
        'jersey',
        'accessories',
        'hats',
        'socks',
        'scarves',
        'sale'
      ]);

      const candidateCollections =
        this.pipeSet(item.dataset.collections);

      let matches = 0;

      candidateCollections.forEach(handle => {
        if (
          this.sourceCollections.has(handle) &&
          !genericCollections.has(handle)
        ) {
          matches += 1;
        }
      });

      return matches;
    }

    sizeMatchBonus(item) {
      if (!this.sourceSize) return 0;

      const sourceSize =
        this.normalizeSize(this.sourceSize);

      const availableSizes =
        this.normalizedSizeSet(
          item.dataset.availableSizes
        );

      return availableSizes.has(sourceSize)
        ? 12
        : 0;
    }

    randomJitter(productId) {
      /*
        One seed per page load:
        suggestions remain stable while the customer is looking
        at the page, but can vary on a later visit/reload.
      */
      if (!window.faRecommendationSeed) {
        window.faRecommendationSeed =
          Math.floor(Math.random() * 1000000000);
      }

      const input =
        `${productId}-${window.faRecommendationSeed}`;

      let hash = 0;

      for (let index = 0; index < input.length; index += 1) {
        hash =
          ((hash << 5) - hash) +
          input.charCodeAt(index);

        hash |= 0;
      }

      return Math.abs(hash % 1200) / 100;
    }

    scoreFallback(item) {
      const family = this.getFamily(item);

      /*
        Same-category alternatives are not useful cart cross-sells.
        Accessories are the exception because two accessories can
        genuinely complement each other.
      */
      if (
        family === this.sourceFamily &&
        this.sourceFamily !== 'accessories'
      ) {
        return -Infinity;
      }

      const preferences =
        this.getFamilyPreferences();

      const preferenceIndex =
        preferences.indexOf(family);

      let score = 0;

      if (preferenceIndex !== -1) {
        score += 90 - (preferenceIndex * 12);
      } else if (
        family !== 'unknown' &&
        family !== this.sourceFamily
      ) {
        score += 35;
      }

      score +=
        this.meaningfulSharedCollections(item) * 24;

      score +=
        this.sizeMatchBonus(item);

      score +=
        this.randomJitter(this.getProductId(item));

      return score;
    }

    parseRecommendationResponse(text) {
      const documentHTML =
        new DOMParser().parseFromString(
          text,
          'text/html'
        );

      const responseComponent =
        documentHTML.querySelector(
          `product-recommendations[data-recommendation-area="${this.dataset.recommendationArea}"]`
        ) ||
        documentHTML.querySelector(
          'product-recommendations'
        );

      if (!responseComponent) {
        return {
          complementary: [],
          fallback: []
        };
      }

      const complementary =
        Array.from(
          responseComponent.querySelectorAll(
            '[data-recommendations] > .product-recommendations__item'
          )
        );

      const fallbackTemplate =
        responseComponent.querySelector(
          'template[data-fallback-candidates]'
        );

      const fallback =
        fallbackTemplate
          ? Array.from(
              fallbackTemplate.content.querySelectorAll(
                '.product-recommendations__item'
              )
            )
          : [];

      return {
        complementary,
        fallback
      };
    }

    async fetchCandidates() {
      if (!this.dataset.url) {
        return {
          complementary: [],
          fallback: []
        };
      }

      try {
        const response =
          await fetch(this.dataset.url);

        if (!response.ok) {
          return {
            complementary: [],
            fallback: []
          };
        }

        const text =
          await response.text();

        return this.parseRecommendationResponse(text);
      } catch (error) {
        console.error(
          'Product recommendations could not be loaded:',
          error
        );

        return {
          complementary: [],
          fallback: []
        };
      }
    }

    selectRecommendations(
      complementaryItems,
      fallbackItems
    ) {
      if (this.shirtOfferActive) {
        // All eligible full-price shirts are candidates. Do not restrict
        // to Search & Discovery complements, which may include trousers.
        const uniqueIds = new Set();
        return [...complementaryItems, ...fallbackItems]
          .filter(item => {
            const id = this.getProductId(item);
            if (!id || uniqueIds.has(id) || !this.isEligible(item)) {
              return false;
            }
            uniqueIds.add(id);
            return true;
          })
          .map(item => ({
            item,
            score: (
              this.meaningfulSharedCollections(item) * 12 +
              this.sizeMatchBonus(item) +
              this.randomJitter(this.getProductId(item))
            )
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, this.limit)
          .map(candidate => candidate.item);
      }

      const selected = [];
      const selectedIds = new Set();
      const selectedFamilies = new Set();

      const addItem = item => {
        if (selected.length >= this.limit) {
          return false;
        }

        const productId =
          this.getProductId(item);

        if (
          !productId ||
          selectedIds.has(productId) ||
          !this.isEligible(item)
        ) {
          return false;
        }

        selected.push(item);
        selectedIds.add(productId);
        selectedFamilies.add(
          this.getFamily(item)
        );

        return true;
      };

      /*
        TIER 1
        Human-curated Search & Discovery complements.
        Preserve Shopify's manual order exactly.
      */
      complementaryItems.forEach(item => {
        addItem(item);
      });

      if (selected.length >= this.limit) {
        return selected;
      }

      /*
        TIER 2
        Cross-category fallback candidates.

        Score first, then prefer a different category for each
        additional slot so we don't swap "three shirts" for
        "three trousers".
      */
      const scoredFallback =
        fallbackItems
          .filter(item => {
            const productId =
              this.getProductId(item);

            return (
              productId &&
              !selectedIds.has(productId) &&
              this.isEligible(item)
            );
          })
          .map(item => ({
            item,
            score: this.scoreFallback(item)
          }))
          .filter(candidate =>
            Number.isFinite(candidate.score) &&
            candidate.score >= 50
          )
          .sort(
            (a, b) => b.score - a.score
          );

      /*
        First pass: maximise category diversity.
      */
      scoredFallback.forEach(candidate => {
        if (selected.length >= this.limit) return;

        const family =
          this.getFamily(candidate.item);

        if (
          family !== 'unknown' &&
          selectedFamilies.has(family)
        ) {
          return;
        }

        addItem(candidate.item);
      });

      /*
        Second pass: if a genuinely relevant candidate remains,
        allow another product from an already represented family.
        We still never force a weak recommendation just to hit 3.
      */
      if (selected.length < this.limit) {
        scoredFallback.forEach(candidate => {
          if (selected.length >= this.limit) return;

          addItem(candidate.item);
        });
      }

      return selected;
    }

    async performRecommendations() {
      const recommendations =
        this.querySelector(
          '[data-recommendations]'
        );

      if (!recommendations) return;

      const {
        complementary,
        fallback
      } = await this.fetchCandidates();

      const selected =
        this.selectRecommendations(
          complementary,
          fallback
        );

      if (!selected.length) {
        return;
      }

      recommendations.innerHTML = '';

      selected.forEach(item => {
        recommendations.appendChild(item);
      });
      this.prepareDirectAddButtons(selected);

      this.classList.remove('hidden');

      const cartDrawerItemsFull =
        document.querySelector(
          '.cart-drawer-items__full'
        );

      if (cartDrawerItemsFull) {
        cartDrawerItemsFull.classList.remove(
          'cart-drawer-items__full'
        );
      }

      this.initSlider();

      /*
        Quick-add already exists globally. Re-initialise only
        after the selected recommendation nodes are in the DOM.
      */
      setTimeout(() => {
        const quickCartDrawer =
          document.querySelector(
            'quick-cart-drawer'
          );

        if (quickCartDrawer) {
          quickCartDrawer.init();
        } else {
          this.querySelectorAll(
            '.quick-cart-drawer__trigger'
          ).forEach(element => {
            element.remove();
          });
        }
      }, 250);
    }

    initSlider() {
      const swiper =
        this.querySelector('.swiper');

      if (!swiper) return;

      if (this.slider) {
        this.slider.destroy(
          true,
          true
        );
      }

      this.slider = new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 16
      });
    }
  }

  customElements.define(
    'product-recommendations',
    ProductRecommendations
  );
}